import { useCallback, useEffect, useState } from 'react'
import { useAuth } from './lib/auth'
import { supabase } from './lib/supabase'
import { Connexion } from './screens/Connexion'
import { MotDePasse } from './screens/MotDePasse'
import { Chantiers } from './screens/Chantiers'
import { Chantier } from './screens/Chantier'
import { Accueil } from './screens/Accueil'
import { Ventes } from './screens/Ventes'
import { modulesDisponibles, type CleModule } from './lib/modules'
import type { Chantier as TChantier } from './lib/types'
import { useHistorique } from './lib/historique'

export function App() {
  const { session, profil, chargement, profilIncertain, relireProfil, deconnexion } = useAuth()
  const [ouvert, setOuvert] = useState<TChantier | null>(null)
  const [cle, setCle] = useState(0)
  useHistorique(ouvert, setOuvert, (c) => c?.id ?? null)
  // Module affiché. L'accueil s'affiche à chaque ouverture, seulement pour
  // ceux qui ont au moins deux modules (décision du 4 nov. 2026).
  const [module, setModule] = useState<CleModule | 'accueil'>('accueil')
  useHistorique(module, setModule)
  // Nouvelle connexion (autre compte ou retour après déconnexion) : accueil.
  const idProfil = profil?.id
  useEffect(() => {
    if (idProfil) setModule('accueil')
  }, [idProfil])
  const [suiviCible, setSuiviCible] = useState<string | null>(null)
  const suiviCibleTraite = useCallback(() => setSuiviCible(null), [])
  const [erreurLien, setErreurLien] = useState<string | null>(null)

  // Lien « Choisis ton mot de passe » (invitation ou nouveau lien) : ?bienvenue=<jeton>.
  // Le jeton ouvre la session, puis on demande le mot de passe.
  const [bienvenue, setBienvenue] = useState<'verif' | 'choisir' | null>(() =>
    new URLSearchParams(window.location.search).has('bienvenue') ? 'verif' : null
  )
  const [erreurBienvenue, setErreurBienvenue] = useState<string | null>(null)
  useEffect(() => {
    const jeton = new URLSearchParams(window.location.search).get('bienvenue')
    if (!jeton) return
    history.replaceState(history.state, '', window.location.pathname)
    void supabase.auth.verifyOtp({ token_hash: jeton, type: 'recovery' }).then(({ error }) => {
      if (error) {
        const m =
          /Failed to fetch|NetworkError/i.test(error.message)
            ? "Aucune connexion : impossible de vérifier le lien. Rouvre-le depuis le courriel quand le réseau sera revenu."
            : "Ce lien n'est plus valide (déjà utilisé ou expiré). Demande à Jasmin de t'en envoyer un nouveau."
        setErreurBienvenue(m)
        setErreurLien(m)
        setBienvenue(null)
      } else {
        setBienvenue('choisir')
      }
    })
  }, [])

  /** Ouvre un chantier (et une fiche de suivi) : cloche ou lien du courriel. */
  const aller = useCallback(async (chantierId: string, suiviId: string | null) => {
    setErreurLien(null)
    const { data } = await supabase.from('chantiers').select('*').eq('id', chantierId).maybeSingle()
    if (!data) {
      setErreurLien("Ce chantier n'est pas accessible (pas de réseau, ou tu n'y es pas assigné).")
      return
    }
    setOuvert((o) => (o?.id === chantierId ? o : (data as TChantier)))
    setSuiviCible(suiviId)
  }, [])

  // Lien du courriel du matin : ?chantier=…&suivi=…
  useEffect(() => {
    if (!session || !profil) return
    const p = new URLSearchParams(window.location.search)
    const c = p.get('chantier')
    if (!c) return
    history.replaceState(history.state, '', window.location.pathname)
    void aller(c, p.get('suivi'))
  }, [session, profil, aller])

  if (bienvenue === 'verif') {
    return (
      <div className="app">
        <p className="vide" style={{ marginTop: 80 }}>Vérification du lien…</p>
      </div>
    )
  }

  if (bienvenue === 'choisir' && session) {
    return <MotDePasse courriel={session.user.email ?? null} termine={() => setBienvenue(null)} />
  }

  if (chargement) {
    return (
      <div className="app">
        <p className="vide" style={{ marginTop: 80 }}>Chargement…</p>
      </div>
    )
  }

  if (!session)
    return (
      <>
        {erreurBienvenue && (
          <p className="avis erreur" style={{ margin: 12 }}>
            {erreurBienvenue}
          </p>
        )}
        <Connexion />
      </>
    )

  if (!profil) {
    return (
      <div className="app">
        <div className="corps">
          <p className="avis" style={{ marginTop: 40 }}>
            {profilIncertain
              ? "Impossible de lire ton profil pour l'instant (réseau). Réessaie dans un moment."
              : "Ton compte existe, mais son profil n'a pas encore été créé en base. Préviens l'administrateur."}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="bouton" onClick={relireProfil}>
              Réessayer
            </button>
            <button className="bouton creux" onClick={() => void deconnexion()}>
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!profil.actif) {
    return (
      <div className="app">
        <div className="corps">
          <p className="avis" style={{ marginTop: 40 }}>
            <strong>Compte en attente d'activation.</strong>
            <br />
            Ton compte ({profil.courriel}) a bien été créé, mais il doit être activé par Jasmin avant que tu puisses
            voir les chantiers. Préviens-le, puis touche « Réessayer ».
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="bouton" onClick={relireProfil}>
              Réessayer
            </button>
            <button className="bouton creux" onClick={() => void deconnexion()}>
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (ouvert) {
    return (
      <Chantier
        key={ouvert.id}
        chantier={ouvert}
        suiviCible={suiviCible}
        suiviCibleTraite={suiviCibleTraite}
        aller={(c, s) => void aller(c, s)}
        fermer={() => {
          setOuvert(null)
          setCle((k) => k + 1) // la liste se recharge en revenant
        }}
        recharger={async () => {
          // Relit le chantier sans le fermer : on reste sur l'onglet en cours.
          const { data } = await supabase.from('chantiers').select('*').eq('id', ouvert.id).single()
          if (data) setOuvert(data as TChantier)
        }}
      />
    )
  }

  const dispo = modulesDisponibles(profil)
  const plusieurs = dispo.length >= 2
  const actuel: CleModule | 'accueil' =
    !plusieurs || (module !== 'accueil' && !dispo.some((m) => m.cle === module)) ? dispo[0]?.cle ?? 'chantiers' : module
  const versAccueil = plusieurs ? () => setModule('accueil') : undefined

  return (
    <>
      {erreurLien && (
        <p className="avis erreur" style={{ margin: 12 }} onClick={() => setErreurLien(null)}>
          {erreurLien}
        </p>
      )}
      {actuel === 'accueil' && <Accueil ouvrir={setModule} aller={(c, s) => void aller(c, s)} />}
      {actuel === 'chantiers' && (
        <Chantiers key={cle} ouvrir={setOuvert} aller={(c, s) => void aller(c, s)} accueil={versAccueil} />
      )}
      {actuel === 'crm' && <Ventes fermer={() => setModule('accueil')} />}
    </>
  )
}
